import { CSSProperties, ReactNode, useId } from "react";
import {
	Label,
	Pie,
	PieChart,
	PieLabelRenderProps,
	ResponsiveContainer,
	Tooltip,
} from "recharts";
import { formatCount } from "../dashboardConfig";
import ChartTooltip from "./ChartTooltip";

interface DonutSlice {
	id: string;
	label: string;
	value: number;
	color: string;
}

interface DonutChartProps {
	/** In drawing order, clockwise from 12 o'clock. Empty slices keep their label. */
	slices: DonutSlice[];
	total: number;
	/** A second line under the total, e.g. the master tenant's share. */
	caption?: string;
	/** Label each slice with a leader line instead of relying on a legend. */
	leaderLabels?: boolean;
	/** Drawn beside the donut, e.g. its legend. */
	aside?: ReactNode;
	/** An extra tooltip line for a slice. */
	sliceDetail?: (id: string) => string | undefined;
	/** Text alternative for screen readers. */
	summary: string;
}

/** The hole is 52% of the outer radius; leader labels need room around the ring. */
const INNER_RATIO = 0.52;
const OUTER_PERCENT = { plain: 86, withLeaders: 60 };
const START_ANGLE = 90;
const RADIAN = Math.PI / 180;
/** How far past the ring a leader line bends, and the height of a name-and-value label. */
const ELBOW = 12;
const LABEL_HEIGHT = 34;
const LABEL_BOTTOM_MARGIN = 18;
const HOLE = [{ value: 1 }];

interface LeaderPosition {
	cos: number;
	sin: number;
	/** Vertical position of the label line, relative to the centre. */
	y: number;
}

/** Labels of slices on the right half of the ring go to the chart's right side. */
const isOnRight = ({ cos }: { cos: number }) => cos >= 0;

/**
 * Places each slice's label beside the ring at its slice's angle, then
 * spreads the labels on each side apart, so small neighbouring slices (a few
 * drafts next to a few open lessons) never print over each other.
 */
const layoutLeaderLabels = (
	slices: DonutSlice[],
	total: number,
	radius: number,
	maxY: number
): Map<string, LeaderPosition> => {
	let angle = START_ANGLE;
	const positions = slices.map((slice) => {
		const span = total ? (360 * slice.value) / total : 0;
		const midAngle = angle - span / 2;
		angle -= span;
		const cos = Math.cos(-midAngle * RADIAN);
		const sin = Math.sin(-midAngle * RADIAN);
		return { id: slice.id, cos, sin, y: (radius + ELBOW) * sin };
	});

	for (const rightSide of [true, false]) {
		const side = positions
			.filter((position) => isOnRight(position) === rightSide)
			.sort((a, b) => a.y - b.y);
		// Push overlapping labels down, then pull them back up from the bottom edge.
		for (let i = 1; i < side.length; i++) {
			side[i].y = Math.max(side[i].y, side[i - 1].y + LABEL_HEIGHT);
		}
		for (let i = side.length - 1; i >= 0; i--) {
			const limit = i === side.length - 1 ? maxY : side[i + 1].y - LABEL_HEIGHT;
			side[i].y = Math.min(side[i].y, limit);
		}
	}

	return new Map(positions.map(({ id, ...position }) => [id, position]));
};

/** A flat donut with the total in the hole. */
function DonutChart({
	slices,
	total,
	caption,
	leaderLabels = false,
	aside,
	sliceDetail,
	summary,
}: DonutChartProps) {
	const captionId = useId();
	const outer = leaderLabels ? OUTER_PERCENT.withLeaders : OUTER_PERCENT.plain;
	const inner = outer * INNER_RATIO;
	// Recharts colours each sector from its datum's `fill`.
	const sectors = slices.map((slice) => ({ ...slice, fill: slice.color }));

	/** A line from the slice out to the side of the chart, the name above it and the value below. */
	const renderLeaderLabel = (props: PieLabelRenderProps) => {
		const cx = Number(props.cx);
		const cy = Number(props.cy);
		const radius = Number(props.outerRadius);
		const slice = props.payload as DonutSlice;
		const position = layoutLeaderLabels(
			slices,
			total,
			radius,
			cy - LABEL_BOTTOM_MARGIN
		).get(slice.id);
		if (!position) return null;

		const { cos, sin } = position;
		const y = cy + position.y;
		const toRight = isOnRight(position);
		// The chart is centred, so its sides are at 0 and twice the centre.
		const endX = toRight ? cx * 2 - 4 : 4;
		const anchor = toRight ? "end" : "start";
		const points = [
			[cx + radius * cos, cy + radius * sin],
			[cx + (radius + ELBOW) * cos, cy + (radius + ELBOW) * sin],
			[endX, y],
		];

		return (
			<g>
				<polyline
					className="dashboard-donut__leader"
					points={points.map((point) => point.join(",")).join(" ")}
				/>
				<text
					x={endX}
					y={y - 5}
					textAnchor={anchor}
					className="dashboard-donut__leader-label"
				>
					{slice.label}
				</text>
				<text
					x={endX}
					y={y + 15}
					textAnchor={anchor}
					className="dashboard-donut__leader-value"
					style={{ "--series-color": slice.color } as CSSProperties}
				>
					{formatCount(slice.value)}
				</text>
			</g>
		);
	};

	const renderCentre = ({ viewBox }: { viewBox?: unknown }) => {
		const { cx, cy } = (viewBox ?? {}) as { cx?: number; cy?: number };
		if (typeof cx !== "number" || typeof cy !== "number") return null;
		return (
			<text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
				<tspan
					x={cx}
					dy={caption ? "-0.3em" : 0}
					className="dashboard-donut__total"
				>
					{formatCount(total)}
				</tspan>
				{caption && (
					<tspan x={cx} dy="2em" className="dashboard-donut__caption">
						{caption}
					</tspan>
				)}
			</text>
		);
	};

	return (
		<figure className="dashboard-chart">
			<figcaption id={captionId} className="sr-only">
				{summary}
			</figcaption>
			<div className="dashboard-donut">
				<div className="dashboard-donut__chart">
					<ResponsiveContainer width="100%" height="100%" minHeight={140}>
						{/* The label names the keyboard-focusable chart surface. */}
						<PieChart aria-labelledby={captionId}>
							{/* Decorative: the hole behind the total, out of the tab order. */}
							<Pie
								data={HOLE}
								dataKey="value"
								innerRadius={0}
								outerRadius={`${inner}%`}
								className="dashboard-donut__hole"
								stroke="none"
								rootTabIndex={-1}
								isAnimationActive={false}
							>
								<Label position="center" content={renderCentre} />
							</Pie>
							<Pie
								data={sectors}
								dataKey="value"
								nameKey="label"
								innerRadius={`${inner}%`}
								outerRadius={`${outer}%`}
								startAngle={START_ANGLE}
								endAngle={START_ANGLE - 360}
								stroke="none"
								// Pie has no key handling of its own; the chart surface is
								// the one keyboard stop.
								rootTabIndex={-1}
								label={leaderLabels ? renderLeaderLabel : false}
								labelLine={false}
							/>
							<Tooltip
								content={({ active, payload }) => {
									const slice = slices.find(
										(item) => item.label === payload?.[0]?.name
									);
									if (!active || !slice) return null;
									const percent = total
										? Math.round((slice.value / total) * 100)
										: 0;
									const detail = sliceDetail?.(slice.id);
									return (
										<ChartTooltip
											rows={[
												{
													color: slice.color,
													text: `${slice.label} | ${formatCount(slice.value)} (${percent}%)`,
												},
											]}
											details={detail ? [detail] : []}
										/>
									);
								}}
							/>
						</PieChart>
					</ResponsiveContainer>
				</div>
				{aside && <div className="dashboard-donut__legend">{aside}</div>}
			</div>
		</figure>
	);
}

export default DonutChart;
